<script lang="ts">
  import { onMount } from 'svelte';
  import { listKeys, createKey, revokeKey, type ApiKey } from '../account';
  import { ApiError } from '../api';

  let keys = $state<ApiKey[]>([]);
  let loaded = $state(false);
  let err = $state('');

  let creating = $state(false);
  let name = $state('');
  let fresh = $state<{ prefix: string; plaintext: string } | null>(null);
  let copied = $state(false);

  onMount(load);

  async function load() {
    try {
      keys = (await listKeys()).keys;
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'Could not load keys.';
    } finally {
      loaded = true;
    }
  }

  async function create() {
    if (!name.trim() || creating) return;
    creating = true;
    err = '';
    try {
      const r = await createKey(name.trim());
      fresh = { prefix: r.key.prefix, plaintext: r.plaintext };
      name = '';
      await load();
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'Could not create the key.';
    } finally {
      creating = false;
    }
  }

  async function copy() {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh.plaintext);
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch {
      /* clipboard blocked — the key is shown, user can select it */
    }
  }

  async function revoke(id: string) {
    try {
      await revokeKey(id);
      await load();
    } catch (e) {
      err = e instanceof ApiError ? e.message : 'Could not revoke.';
    }
  }

  const active = $derived(keys.filter((k) => k.active));
</script>

<div class="card">
  <p class="eyebrow">API keys</p>
  <p class="muted">For wiring the Kip connector into the desktop app.</p>

  {#if fresh}
    <div class="fresh">
      <p class="fresh-label">Copy this now — it won't be shown again.</p>
      <code>{fresh.plaintext}</code>
      <div class="fresh-row">
        <button class="ghost" onclick={copy}>{copied ? 'Copied' : 'Copy'}</button>
        <button class="ghost" onclick={() => (fresh = null)}>Done</button>
      </div>
    </div>
  {/if}

  {#if loaded && active.length}
    <ul class="keys">
      {#each active as k (k.id)}
        <li>
          <span class="k-name">{k.name}</span>
          <code class="k-prefix">{k.prefix}…</code>
          <button class="revoke" onclick={() => revoke(k.id)}>Revoke</button>
        </li>
      {/each}
    </ul>
  {:else if loaded}
    <p class="muted">No active keys.</p>
  {/if}

  {#if creating || !fresh}
    <form
      class="new"
      onsubmit={(e) => {
        e.preventDefault();
        void create();
      }}
    >
      <input bind:value={name} placeholder="Key name (e.g. work laptop)" maxlength="80" />
      <button class="cta" type="submit" disabled={!name.trim() || creating}>
        {creating ? 'Creating…' : 'New key'}
      </button>
    </form>
  {/if}

  {#if err}<p class="err">{err}</p>{/if}
</div>

<style>
  .card {
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 16px;
    margin-bottom: 14px;
    background: var(--cream-2);
    box-shadow: var(--shadow-soft);
  }
  .eyebrow {
    margin-bottom: 6px;
  }
  .muted {
    color: var(--ink-soft);
    font-size: 14px;
    margin-top: 4px;
  }
  .fresh {
    margin: 12px 0;
    padding: 12px;
    border: 1px solid var(--yolk-deep);
    border-radius: 10px;
    background: var(--paper);
  }
  .fresh-label {
    font-size: 12px;
    color: var(--ink-soft);
    margin-bottom: 8px;
  }
  .fresh code {
    display: block;
    font-family: var(--mono);
    font-size: 12px;
    word-break: break-all;
    background: var(--cream);
    padding: 8px;
    border-radius: 6px;
  }
  .fresh-row {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
  .keys {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 12px 0;
  }
  .keys li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
  }
  .k-name {
    flex: 1;
    font-family: var(--serif);
  }
  .k-prefix {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--ink-faint);
  }
  .revoke {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--rooster);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 3px 8px;
  }
  .new {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }
  .new input {
    flex: 1;
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 9px 12px;
    background: var(--cream-2);
    font-size: 16px;
    min-width: 0;
  }
  .cta,
  .ghost {
    border-radius: 10px;
    padding: 9px 14px;
    font-family: var(--mono);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
    white-space: nowrap;
  }
  .cta {
    background: var(--rooster);
    color: var(--cream);
  }
  .cta:disabled {
    opacity: 0.4;
  }
  .ghost {
    border: 1px solid var(--line);
    color: var(--ink-soft);
  }
  .err {
    color: var(--rooster-deep);
    font-size: 13px;
    margin-top: 10px;
  }
</style>
